import {
  BpkCardV2,
  CARD_V2_SURFACE_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-card';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import FlightIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/flight';
import NewWindowIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/new-window';
import { BpkSpacing } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  generateTrackingAttributes,
  useTranslations,
  useI18n,
} from '@web-platform/shared-travel-tools-utils-frontend';

import { PriceBadge } from '../PriceBadge/PriceBadge';

import STYLES from './FlightCard.module.scss';

const MBD_FLIGHT_CARD = {
  id: 'travel_tools_flight_card',
  name: 'travel_tools_flight_card',
} as const;

export type FlightCardArea = 'map' | 'list';

export type FlightCardProps = {
  destinationAirportCode: string;
  cityName: string;
  countryName: string;
  image: null | {
    url: string;
  };
  price: {
    indicativePrice: number;
    priceDifferencePercentage: number;
  };
  priceLevel: string;
  isDirect: boolean;
  exploreUrl: string;
};

type Props = FlightCardProps & {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  area: FlightCardArea;
  position?: number;
};

const FlightIconWithRtl = withRtlSupport(FlightIcon);
const NewWindowIconWithRtl = withRtlSupport(NewWindowIcon);

export const FlightCard = ({
  area,
  cityName,
  countryName,
  destinationAirportCode,
  exploreUrl,
  image,
  isDirect,
  onMouseEnter,
  onMouseLeave,
  position,
  price,
  priceLevel,
}: Props) => {
  const { translate } = useTranslations();
  const {
    culture: { currency },
    formatCurrency,
  } = useI18n();

  const trackingAttrs = generateTrackingAttributes(
    `${MBD_FLIGHT_CARD.id}_${area}_${destinationAirportCode}`,
    MBD_FLIGHT_CARD.name,
    {
      price_level: priceLevel,
      area,
      is_direct: isDirect,
      indicative_price: Math.round(price.indicativePrice),
      currency,
      ...(position !== undefined ? { position } : {}),
    },
  );
  const onewayLabel = [
    translate('travel_confidence_map_one_way_label'),
    isDirect
      ? translate('travel_confidence_map_price_direct')
      : translate('travel_confidence_map_stops_one_or_more'),
  ].join(' \u2022 ');

  return (
    <BpkLink
      blank
      href={exploreUrl}
      rel="noopener noreferrer"
      data-card-id={destinationAirportCode}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...trackingAttrs}
    >
      <BpkCardV2.Root bgColor={CARD_V2_SURFACE_COLORS.surfaceDefault} data-testid="flight-card">
        <BpkCardV2.Body padding={BpkSpacing.None}>
          <BpkCardV2.Section padding={BpkSpacing.None}>
            <div className={STYLES.content}>
              {image && <img alt="" className={STYLES.image} src={image.url} />}
              <div className={STYLES.info}>
                {priceLevel && (
                  <PriceBadge
                    priceLevel={priceLevel}
                    priceDifferencePercentage={price.priceDifferencePercentage}
                  />
                )}
                <BpkText tagName="p" textStyle={TEXT_STYLES.label1}>
                  {translate('travel_confidence_map_cityName_and_countryName', {
                    cityName,
                    countryName,
                  })}
                </BpkText>
                <div className={STYLES.flightRow}>
                  <FlightIconWithRtl aria-hidden />
                  <BpkText textStyle={TEXT_STYLES.caption}>{onewayLabel}</BpkText>
                  <div className={STYLES.priceRow}>
                    <BpkText textStyle={TEXT_STYLES.label1}>
                      {formatCurrency(price.indicativePrice)}
                    </BpkText>
                    <NewWindowIconWithRtl aria-hidden />
                  </div>
                </div>
              </div>
            </div>
          </BpkCardV2.Section>
        </BpkCardV2.Body>
      </BpkCardV2.Root>
    </BpkLink>
  );
};
